import React, { useState, useEffect, useRef } from 'react';
import { FaTrash } from 'react-icons/fa';
import { ProjectDataService } from '../services/ProjectDataService';
import { ProjectSearchService } from '../services/ProjectSearchService';

const TableRow = ({ row, index, updateRow, deleteRow, isLoading, currentUser, isEditable = true }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const dropdownRef = useRef(null);
  const projectInputRef = useRef(null);

  // Initialize the project search service
  useEffect(() => {
    ProjectSearchService.initialize()
      .catch(err => console.error('Failed to initialize project search service:', err));
  }, []);

  // Handle clicks outside the dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Sync row.projectNumber to searchTerm when it changes externally
  useEffect(() => {
    if (row.projectNumber !== searchTerm) {
      setSearchTerm(row.projectNumber || '');
    }
  }, [row.projectNumber, searchTerm]);

  // Perform search when searchTerm changes (with debounce)
  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2 || !userInteracted || !isEditable) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Don't show dropdown if the current search term exactly matches an existing project number
    // This prevents dropdown from appearing after selection
    if (row.projectNumber && searchTerm === row.projectNumber && row.projectName) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      try {
        setIsSearching(true);
        setHasError(false);
        
        // Use the API-based search service
        const results = await ProjectSearchService.searchProjects(searchTerm);
        
        setSuggestions(results);
        // Only show dropdown if user has explicitly interacted with the field and is editable
        setShowDropdown(results.length > 0 && userInteracted && isEditable);
        setIsSearching(false);
      } catch (error) {
        console.error('Search failed:', error);
        setSuggestions([]);
        setShowDropdown(false);
        setIsSearching(false);
        setHasError(true);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, userInteracted, isEditable, row.projectNumber, row.projectName]);

  const handleProjectNumberChange = (e) => {
    if (!isEditable) {
      e.preventDefault();
      return;
    }
    
    const value = e.target.value;
    updateRow(index, 'projectNumber', value);
    setSearchTerm(value);
    setUserInteracted(true);
    
    // Clear related fields when project number changes
    if (value !== row.projectNumber) {
      updateRow(index, 'projectName', '');
      updateRow(index, 'milestone', '');
      updateRow(index, 'pm', '');
      updateRow(index, 'labor', '');
      updateRow(index, 'pctLaborUsed', '');
    }
  };

  const handleFocus = () => {
    if (!isEditable) return;
    
    // Only show dropdown if:
    // 1. Search term is long enough
    // 2. User has interacted
    // 3. Current search term doesn't match an already selected project
    if (searchTerm && searchTerm.length >= 2 && userInteracted && 
        !(row.projectNumber && searchTerm === row.projectNumber && row.projectName)) {
      setShowDropdown(suggestions.length > 0);
    }
  };

  const handleKeyPress = async (e) => {
    if (!isEditable) {
      e.preventDefault();
      return;
    }
    
    if (e.key === 'Enter') {
      e.preventDefault();
      
      if (showDropdown && suggestions.length > 0) {
        handleProjectSelect(suggestions[0]);
      } else if (searchTerm && searchTerm.length >= 2) {
        triggerSearch();
      }
    } else if (e.key === 'ArrowDown' && showDropdown && suggestions.length > 0) {
      const dropdownItems = dropdownRef.current.querySelectorAll('.suggestion-item');
      if (dropdownItems.length > 0) {
        dropdownItems[0].focus();
      }
    }
  };

  const triggerSearch = async () => {
    if (!searchTerm || searchTerm.length < 2 || !isEditable) return;
    
    try {
      setIsSearching(true);
      setHasError(false);
      
      try {
        const results = await ProjectSearchService.searchProjects(searchTerm);
        
        if (results.length > 0) {
          handleProjectSelect(results[0]);
        } else {
          try {
            const milestone = await ProjectDataService.getMilestoneDetails(searchTerm);
            updateRow(index, 'projectNumber', milestone.project_number);
            updateRow(index, 'projectName', milestone.project_name);
            updateRow(index, 'milestone', milestone.milestone_name);
            updateRow(index, 'pm', milestone.project_manager);
            updateRow(index, 'labor', milestone.contract_labor);
            updateRow(index, 'pctLaborUsed', milestone.forecast_pm_labor); //changed
          } catch (apiError) {
            console.error('Failed to find project in API or fallback service:', apiError);
            setHasError(true);
          }
        }
      } catch (searchError) {
        console.error('Error searching for projects:', searchError);
        setHasError(true);
      }
      
      setIsSearching(false);
    } catch (error) {
      console.error('Failed to search for projects:', error);
      setIsSearching(false);
      setHasError(true);
    }
  };

  const handleProjectSelect = async (project) => {
    if (!isEditable) return;
    
    try {
      console.log("API project data:", project);
      console.log("Raw Pct Labor Used:", project['Pct Labor Used']);
      
      const pctLaborUsed = parseFloat(project['Pct Labor Used']) || 0;
      console.log("Parsed pctLaborUsed:", pctLaborUsed);
      
      // Update all the row data
      updateRow(index, 'projectNumber', project['Project Number']);
      updateRow(index, 'projectName', project['Project Name']);
      updateRow(index, 'milestone', project['Milestone']);
      updateRow(index, 'pm', project['PM']);
      updateRow(index, 'labor', project['Labor']);
      updateRow(index, 'pctLaborUsed', pctLaborUsed);
      updateRow(index, '_projectData', project);
      
      // Clear dropdown state immediately
      setShowDropdown(false);
      setSuggestions([]);
      setHasError(false);
      setUserInteracted(false); // This prevents the search effect from triggering
      
      // Use a small timeout to ensure state updates are processed
      setTimeout(() => {
        setSearchTerm(project['Project Number']);
        // Blur the input to remove focus
        if (projectInputRef.current) {
          projectInputRef.current.blur();
        }
      }, 10);
    } catch (error) {
      console.error('Failed to select project:', error);
      setHasError(true);
    }
  };

  // Updated hours change handler
  const handleHoursChange = (e) => {
    if (!isEditable) {
      e.preventDefault();
      return;
    }
    updateRow(index, 'hours', Math.max(0, e.target.value));
  };

  // Updated remarks change handler
  const handleRemarksChange = (e) => {
    if (!isEditable) {
      e.preventDefault();
      return;
    }
    updateRow(index, 'remarks', e.target.value);
  };

  // Updated delete handler
  const handleDelete = (e) => {
    if (!isEditable) {
      e.preventDefault();
      return;
    }
    deleteRow(index);
  };

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });


  const highlightMatch = (text, query) => {
    if (!query || !text) return text;
    
    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    
    if (!textLower.includes(queryLower)) return text;
    
    // Find all matches for better highlighting
    const parts = [];
    let lastIndex = 0;
    let currentIndex = textLower.indexOf(queryLower, lastIndex);
    
    while (currentIndex !== -1) {
      // Add text before the match
      if (currentIndex > lastIndex) {
        parts.push(text.substring(lastIndex, currentIndex));
      }
      
      // Add the highlighted match
      parts.push(
        <span key={`highlight-${currentIndex}`} className="highlight">
          {text.substring(currentIndex, currentIndex + query.length)}
        </span>
      );
      
      lastIndex = currentIndex + query.length;
      currentIndex = textLower.indexOf(queryLower, lastIndex);
    }
    
    // Add remaining text after the last match
    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }
    
    return parts.length > 1 ? <>{parts}</> : text;
  };

  return (
    <tr className={hasError ? 'row-error' : ''}>
      <td className="project-number-cell">
        <div className="dropdown-container" ref={dropdownRef}>
          <div className="search-input-container">
            <input
              type="text"
              value={searchTerm}
              onChange={handleProjectNumberChange}
              onKeyDown={handleKeyPress}
              onFocus={handleFocus}
              onClick={() => isEditable && setUserInteracted(true)}
              placeholder={isLoading ? "Loading..." : !isEditable ? "Locked" : "Search by number, name, or milestone..."}
              disabled={isLoading || !isEditable}
              readOnly={!isEditable}
              ref={projectInputRef}
              className={hasError ? 'input-error' : ''}
              autoComplete="off"
              title={!isEditable ? 'This week is locked - cannot edit project' : ''}
            />
          </div>
          {isSearching && isEditable && <div className="search-indicator">Searching...</div>}
          {hasError && isEditable && <div className="search-error">Project not found</div>}
          {showDropdown && suggestions.length > 0 && isEditable && (
            <div className="suggestions-dropdown">
              {suggestions.map((project, i) => (
                <div
                  key={i}
                  className="suggestion-item"
                  onClick={() => handleProjectSelect(project)}
                  tabIndex="0"
                  onKeyDown={(e) => e.key === 'Enter' && handleProjectSelect(project)}
                >
                  <div className="suggestion-project-name">
                    {highlightMatch(project['Project Name'], searchTerm)}
                  </div>
                  <div className="suggestion-milestone">
                    {highlightMatch(project['Milestone'], searchTerm)}
                  </div>
                  {i < suggestions.length - 1 && <div className="suggestion-separator"></div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </td>
      <td>
        <input 
          type="text" 
          value={row.projectName || ''} 
          readOnly 
          title={!isEditable ? 'This week is locked' : ''}
        />
      </td>
      <td>
        <input 
          type="text" 
          value={row.projectNumber?.startsWith('0000-0000') ? '-' : (row.milestone || '')} 
          readOnly 
          title={!isEditable ? 'This week is locked' : ''}
        />
      </td>
      <td>
        <input 
          type="text" 
          value={row.projectNumber?.startsWith('0000-0000') ? '-' : (row.pm || '')} 
          readOnly 
          className="centered-input" 
          title={!isEditable ? 'This week is locked' : ''}
        />
      </td>
      <td>
        <input
          type="text"
          value={row.projectNumber?.startsWith('0000-0000') ? '-' : 
                (row.labor ? formatter.format(row.labor) : '')}
          readOnly
          className="centered-input"
          title={!isEditable ? 'This week is locked' : ''}
        />
      </td>
      <td>
        <input 
          type="text" 
          value={row.projectNumber?.startsWith('0000-0000') ? '-' : 
          (row.pctLaborUsed ? `${row.pctLaborUsed}%` : '')}
          // value={row.projectNumber?.startsWith('0000-0000') ? '-' : 
          //       (row.pctLaborUsed)} //? percentFormatter(row.pctLaborUsed)
          readOnly 
          className="centered-input"
          title={!isEditable ? 'This week is locked' : ''}
        />
      </td>
      <td>
        <input 
          type="number" 
          value={row.hours || ''} 
          onChange={handleHoursChange}
          min="0"
          step="0.5"
          placeholder={!isEditable ? '' : '0'}
          disabled={!isEditable}
          readOnly={!isEditable}
          title={!isEditable ? 'This week is locked - cannot edit hours' : ''}
        />
      </td>
      <td>
        <input 
          type="text"
          value={row.remarks || ''}
          onChange={handleRemarksChange}
          placeholder={!isEditable ? '' : 'Add remarks...'}
          disabled={!isEditable}
          readOnly={!isEditable}
          title={!isEditable ? 'This week is locked - cannot edit remarks' : ''}
        />
      </td>
      <td>
        <button 
          onClick={handleDelete} 
          className="delete-btn" 
          type="button" 
          disabled={!isEditable}
          title={!isEditable ? 'This week is locked - cannot delete rows' : 'Delete row'}
        >
          <FaTrash />
        </button>
      </td>
    </tr>
  );
};

export default TableRow;